// 📄 projectService.ts
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  writeBatch,
  Unsubscribe
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functionsClient } from '../firebase';
import { queueEmail } from './mailService.ts';

// 'soporte' (T1 de plan-tickets-proyecto.md): lado consultora; recibe los
// tickets de la Mesa de ayuda del proyecto y los asigna internamente.
export type ProjectRole = 'PM' | 'tecnico' | 'funcional' | 'soporte' | 'cliente';

// Fuente única de las opciones de rol — la usan tanto el formulario de
// invitar como el editor de rol de un miembro ya agregado, para que nunca
// queden desincronizados.
export const ROLE_OPTIONS: { value: ProjectRole; label: string }[] = [
  { value: 'PM', label: 'Project Manager' },
  { value: 'tecnico', label: 'Consultor Técnico' },
  { value: 'funcional', label: 'Consultor Funcional' },
  { value: 'soporte', label: 'Soporte (mesa de ayuda)' },
  { value: 'cliente', label: 'Cliente (solo lectura)' }
];

export interface ProjectMember {
  userId: string;
  rol: ProjectRole;
  email: string;
  // Inactivar (en vez de borrar) para conservar el registro por auditoría
  // — ver deactivateMember/reactivateMember. `undefined` en cualquier doc
  // creado antes de este campo se trata como activo (true) en todo lector.
  activo?: boolean;
  // Organigrama (ver OrganigramaProyecto.tsx) — PURAMENTE descriptivo, no
  // toca ningún permiso: `cargoOrganigrama` es un cargo de exhibición
  // (ej. "PM", "Basis") elegido de un catálogo fijo según de qué lado está
  // la persona (`rol === 'cliente'` → catálogo del cliente, cualquier otro
  // rol → catálogo de la consultora) — independiente del `rol` real, que
  // sigue siendo la única fuente de verdad para accesos/escritura.
  // `responsabilidades` es texto libre (ej. "Acceso a Cloud Connector,
  // crea SRs, acceso a BTP").
  cargoOrganigrama?: string;
  responsabilidades?: string;
}

// projects/{id}/permisos/{uid} — visibilidad de sidebar por miembro (ver
// PermisosMiembroModal.tsx) + quién puede gestionar los permisos de OTROS
// miembros. Vive en subcolección propia (NO dentro de miembros[]) a
// propósito: firestore.rules necesita poder leer "¿esto está oculto/
// puedo gestionar PARA MÍ?" con un path direccionable por uid — un array
// de mapas no se puede "buscar por userId==yo" desde una regla. Además,
// así se puede proteger la escritura de este subrecurso con una regla
// MÁS estricta (dueño del proyecto, o a quien el dueño delegue) que la
// que protege miembros[] en general (invitar/cambiar rol, abierta a
// cualquier no-cliente) — ver puedeGestionarPermisosDeProyecto en
// firestore.rules. `undefined`/doc ausente (miembro invitado antes de
// esta sub-fase, o que nunca tuvo restricciones) se trata como "nada
// oculto, no puede delegar" en todo lector.
export interface MemberPermisos {
  // Lista de OCULTOS (no de visibles) a propósito: un item nuevo que se
  // agregue a futuro en menuConfig.tsx (ej. una 7ma herramienta) queda
  // visible por defecto para todo miembro ya existente, sin tener que
  // volver a tocar cada uno. Ids de MenuItemDef de Funciones/Herramientas/
  // Configuración, y de Proyecto solo "incidentes"/"miembros" — "detalle"/
  // "horas"/"mi-espacio" nunca son ocultables (ver PROYECTO_ITEMS_CONFIGURABLES).
  seccionesOcultas: string[];
  // Slugs de solucionesConfig.ts ("commerce", "slp", ...) que este
  // miembro no puede ver en el panel "Soluciones del Proyecto". NO tiene
  // default por rol — arranca vacío siempre, el dueño lo ajusta caso a caso.
  solucionesOcultas: string[];
  // Sub-fase de delegación (2026-08-11, pedido explícito): por defecto
  // SOLO quien creó el proyecto (`Project.creadoPor`) puede ver/usar el
  // botón "Permisos" de cualquier miembro. El dueño puede delegar esa
  // facultad marcando este flag en el doc de OTRO miembro — a partir de
  // ahí, esa persona también puede gestionar los permisos de cualquiera
  // (incluido, si el dueño lo permite, delegar a un tercero — no hay
  // límite de un solo nivel). SOLO el dueño puede tocar este campo
  // puntual (ver firestore.rules) — alguien delegado no puede
  // auto-otorgarse ni quitarle la delegación a otro.
  puedeGestionarPermisos?: boolean;
}

// Presets de "qué queda oculto por defecto" al invitar según el rol
// elegido — ver Fase 0 de esta sub-fase: se basan en el comportamiento
// REAL ya vigente hoy (soloConsultor en menuConfig.tsx: Config queda
// oculto solo para "cliente"), no en el catálogo de permisos de
// rolePresets.ts (declarativo, nunca validado contra las rules reales).
// El dueño puede ajustar cualquier miembro individualmente después —
// esto es solo el punto de partida al invitar.
// Default de "Miembros" oculto para técnico/funcional (2026-08-20): un
// invitado con cualquiera de esos roles podía ir a Miembros e inactivar a
// CUALQUIERA, incluido quien creó el proyecto — reportado por el equipo
// probando escenarios. El PM sigue viendo/gestionando Miembros por
// default (es el responsable natural del equipo); técnico/funcional lo
// pueden recibir igual, pero a mano, vía el modal "Permisos" (delegación
// existente, ver puedeGestionarPermisosDeProyecto). Segunda capa de
// protección — el creador en sí no se puede inactivar salvo por él mismo
// o un Admin, sin importar quién tenga la sección habilitada — ver
// firestore.rules → match /projects/{projectId} → allow update.
export const ROLE_DEFAULT_SECCIONES_OCULTAS: Record<ProjectRole, string[]> = {
  PM: [],
  tecnico: ['miembros'],
  funcional: ['miembros'],
  cliente: ['config', 'editar']
};

export interface Project {
  id: string;
  // Vacío ("") en proyectos sin cliente (proyecto propio/interno, ver
  // plan-proyectos-generales.md). Todo lo que lo usa es del módulo SAP Ariba.
  clienteId: string;
  // Organización dueña del proyecto (Sub-fase D del refactor de
  // Organizaciones). Todo proyecto creado desde Sub-fase E en adelante lo
  // trae siempre — firestore.rules lo exige en `create`. Opcional en el
  // tipo solo por los proyectos legado ya backfillados en Sub-fase D (que
  // sí lo tienen, pero el tipo se deja opcional por si algún doc muy
  // viejo quedara sin tocar).
  orgId?: string;
  // Sub-fase G2: nombre del cliente DESNORMALIZADO al momento de crear (o
  // editar) el proyecto — clientes.read ahora exige ser miembro de la org
  // dueña del cliente, así que un "guest" (miembro directo de este
  // proyecto, pero de otra org) ya no puede resolver el nombre vía
  // getCliente(clienteId). Ausente en proyectos creados antes de G2 —
  // backfillado por scripts/backfill-cliente-nombre-projects.mjs, y todo
  // lector debe hacer fallback a getCliente() si falta. Puede quedar
  // desactualizado si el cliente se renombra (drift cosmético aceptado).
  clienteNombre?: string;
  nombre: string;
  // Slugs de solucionesConfig.ts ("commerce", "slp", ...). Los proyectos
  // creados antes de esto no tienen el campo — se trata como ["commerce"]
  // en todo lector, para no perder acceso a lo que ya funcionaba.
  solucionesActivas?: string[];
  // Módulos del proyecto (ver modulosConfig.ts): "gestion" siempre, más los
  // especializados ("sap-ariba"). Ausente en proyectos creados antes de
  // esto: se tratan como ["gestion", "sap-ariba"] (modulosDe()).
  modulos?: string[];
  // Plantilla con la que se creó ("general" | "software" | "sap-ariba").
  plantilla?: string;
  // Mesa de ayuda (plan-tickets-proyecto.md): el proyecto recibe los
  // tickets de soporte de su cliente (portal y extensión). Ausente = no.
  recibeTickets?: boolean;
  // Cuántos tickets se han creado (lo mantiene el backend): con tickets, la
  // sección se sigue viendo aunque se apague `recibeTickets`.
  ticketsContador?: number;
  // Áreas para clasificar incidentes, horas, pruebas y documentación en
  // proyectos sin SAP Ariba (ver clasificacionDe en modulosConfig.ts).
  areas?: string[];
  // Resumen de hitos (completados / total), mantenido por hitoService.ts
  // (recalcularAvance) para mostrar el avance sin leer la subcolección.
  avanceHitos?: { completados: number; total: number };
  // Datos del ERP con el que se integra Ariba en este proyecto (ver
  // erpConfig.ts para los valores válidos de erpTipo). Opcionales: los
  // proyectos creados antes de esto no los tienen.
  erpTipo?: string;
  erpVersion?: string;
  // Solo el creador (ver firestore.rules) puede cambiar este campo. Los
  // proyectos creados antes de esto no lo tienen — se tratan como false.
  archivado?: boolean;
  // Cuándo se archivó (null si no lo está). Ver setProjectArchived.
  archivadoEn?: any;
  miembros: ProjectMember[];
  miembrosUids: string[];
  // Subconjunto de miembrosUids cuyo rol es 'cliente' (acceso de solo
  // lectura). Copia plana igual que miembrosUids, para poder chequearla
  // barato en firestore.rules sin recorrer el array de mapas `miembros`.
  miembrosClienteUids?: string[];
  creadoPor: string;
  fechaCreacion?: any;
  commerceIntegration?: any;
  slpIntegration?: any;
  sourcingIntegration?: any;
  buyingIntegration?: any;
  contractsIntegration?: any;
  cigIntegration?: any;
  // "Guía de Integración Estándar" (2026-08-26, ver guiasIntegracionService.ts)
  // — a diferencia de los *Integration de arriba (namespacados por canal,
  // porque una solución puede tener varios canales con ids de paso
  // repetidos), acá alcanza con namespacar por SOLUCIÓN: cada proyecto
  // tiene un único erpTipo, así que solución alcanza para identificar cuál
  // guía corresponde. Shape: { [solucionSlug]: { [pasoId]: true } }.
  guiaIntegracionProgreso?: any;
}

// Plan-proyectos-generales (G1): el cliente solo es obligatorio para SAP
// Ariba (sus credenciales y configuración cuelgan del cliente). Un proyecto
// general puede ser propio, sin cliente.
const validarModulos = (data: { clienteId: string; solucionesActivas: string[]; modulos: string[] }) => {
  if (!data.modulos.includes("gestion")) throw new Error("Todo proyecto incluye Gestión del proyecto.");
  if (data.modulos.includes("sap-ariba")) {
    if (!data.clienteId) throw new Error("Un proyecto SAP Ariba necesita un cliente.");
    if (data.solucionesActivas.length === 0) throw new Error("Selecciona al menos una solución de SAP Ariba.");
  }
};

export const createProject = async (data: {
  clienteId: string;
  // Sub-fase G2: nombre del cliente al momento de crear, desnormalizado
  // en el proyecto — ver comentario de `clienteNombre` en la interfaz
  // Project. El llamador ya lo tiene a mano (viene del mismo `Cliente`
  // que resolvió `clienteId` en el selector).
  clienteNombre: string;
  nombre: string;
  solucionesActivas: string[];
  modulos: string[];
  plantilla?: string;
  areas?: string[];
  erpTipo?: string;
  erpVersion?: string;
  creador: ProjectMember;
  // Sub-fase G1: el llamador decide en qué organización nace el proyecto
  // (org activa del switcher) — ya no se calcula acá adentro. Usar
  // resolveOrgId(orgActual, uid) del lado del llamador para el fallback
  // defensivo a la org personal. firestore.rules exige que quien crea sea
  // owner de este orgId (ver esOwnerDeOrg).
  orgId: string;
}): Promise<string> => {
  if (!data.nombre.trim()) throw new Error("El nombre del proyecto es obligatorio.");
  validarModulos(data);
  if (!data.orgId) throw new Error("orgId es obligatorio.");

  // Precheck de org archivada (2026-08-20) — mismo criterio que el resto:
  // el freno real vive en firestore.rules, esto es solo un mensaje legible.
  const orgParaProyectoSnap = await getDoc(doc(db, "organizations", data.orgId));
  if (orgParaProyectoSnap.exists() && orgParaProyectoSnap.data().archivada === true) {
    throw new Error("Esta organización está archivada — no admite proyectos nuevos. Reactivala primero.");
  }

  // Precheck del límite de proyectos (proyecto_limites_organizacion) —
  // firestore.rules igual lo exige de verdad en el `create` de abajo, pero
  // sin este precheck el usuario solo vería un "Missing or insufficient
  // permissions" genérico en vez de un mensaje que explique qué pasó.
  // Rediseño 2026-08-19: el límite es TOTAL agregado del usuario CREADOR
  // (users/{uid}.limiteProyectos/cantidadProyectosCreados), no por
  // organización — ver proyecto_limites_organizacion.
  const creadorSnap = await getDoc(doc(db, "users", data.creador.userId));
  if (creadorSnap.exists()) {
    const creadorData = creadorSnap.data();
    const limite = creadorData.limiteProyectos ?? Infinity;
    const cantidad = creadorData.cantidadProyectosCreados ?? 0;
    if (cantidad >= limite) {
      throw new Error(`Llegaste al límite de ${limite} proyecto(s) que podés crear. Contactá al administrador para ampliarlo.`);
    }
  }

  const ref = doc(collection(db, "projects"));
  await setDoc(ref, {
    clienteId: data.clienteId,
    clienteNombre: data.clienteNombre,
    orgId: data.orgId,
    nombre: data.nombre.trim(),
    solucionesActivas: data.solucionesActivas,
    modulos: data.modulos,
    plantilla: data.plantilla || "",
    areas: data.areas || [],
    erpTipo: data.erpTipo || "",
    erpVersion: (data.erpVersion || "").trim(),
    archivado: false,
    miembros: [data.creador],
    miembrosUids: [data.creador.userId],
    miembrosClienteUids: data.creador.rol === 'cliente' ? [data.creador.userId] : [],
    creadoPor: data.creador.userId,
    fechaCreacion: serverTimestamp()
  });
  return ref.id;
};

/**
 * Archiva o desarchiva un proyecto. Firestore rechaza este write si quien
 * lo hace no es el creador del proyecto (ni admin) — ver firestore.rules.
 */
export const setProjectArchived = async (projectId: string, archivado: boolean) => {
  const ref = doc(db, "projects", projectId);
  // `archivadoEn`: desde cuándo está cerrado. Con él se cuentan los plazos de
  // conservación de la política de privacidad (functions/services/retencionDatos.js).
  await updateDoc(ref, { archivado, archivadoEn: archivado ? serverTimestamp() : null });
};

export const updateProject = async (
  projectId: string,
  data: { clienteId: string; clienteNombre: string; nombre: string; solucionesActivas: string[]; modulos: string[]; areas?: string[]; erpTipo?: string; erpVersion?: string; recibeTickets?: boolean }
) => {
  if (!data.nombre.trim()) throw new Error("El nombre del proyecto es obligatorio.");
  validarModulos(data);

  const ref = doc(db, "projects", projectId);
  await updateDoc(ref, {
    clienteId: data.clienteId,
    clienteNombre: data.clienteNombre,
    nombre: data.nombre.trim(),
    solucionesActivas: data.solucionesActivas,
    modulos: data.modulos,
    areas: data.areas || [],
    erpTipo: data.erpTipo || "",
    erpVersion: (data.erpVersion || "").trim(),
    // Sin cliente no hay a quién atender: la mesa de ayuda queda apagada.
    recibeTickets: !!data.recibeTickets && !!data.clienteId
  });
};

// Sub-fase K9: cambiar erpTipo (Cloud↔On-Premise) es una acción "pesada" y
// separada del resto de "Editar Proyecto" — solo el creador del proyecto
// puede hacerlo (ver firestore.rules), y queda registrada en un log
// inmutable. writeBatch para que el update del proyecto y el entry de
// auditoría se escriban atómicamente (o los dos, o ninguno).
export const updateErpTipo = async (
  projectId: string, erpTipoActual: string, erpTipoNuevo: string, uid: string
): Promise<void> => {
  const batch = writeBatch(db);
  batch.update(doc(db, "projects", projectId), { erpTipo: erpTipoNuevo });
  batch.set(doc(collection(db, "projects", projectId, "auditoriaErpTipo")), {
    erpTipoAnterior: erpTipoActual,
    erpTipoNuevo,
    cambiadoPor: uid,
    cambiadoEn: serverTimestamp()
  });
  await batch.commit();
};

export interface AuditoriaErpTipoEntry {
  id: string;
  erpTipoAnterior: string;
  erpTipoNuevo: string;
  cambiadoPor: string;
  cambiadoEn: any;
}

export const getAuditoriaErpTipo = async (projectId: string): Promise<AuditoriaErpTipoEntry[]> => {
  const snap = await getDocs(collection(db, "projects", projectId, "auditoriaErpTipo"));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<AuditoriaErpTipoEntry, "id">) }))
    .sort((a, b) => (b.cambiadoEn?.toMillis?.() || 0) - (a.cambiadoEn?.toMillis?.() || 0));
};

export const getProject = async (projectId: string): Promise<Project | null> => {
  const ref = doc(db, "projects", projectId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Project, 'id'>) };
};

export const subscribeProject = (
  projectId: string,
  callback: (project: Project | null) => void
): Unsubscribe => {
  const ref = doc(db, "projects", projectId);
  return onSnapshot(ref, (snap) => {
    if (!snap.exists()) {
      callback(null);
      return;
    }
    callback({ id: snap.id, ...(snap.data() as Omit<Project, 'id'>) });
  });
};

/**
 * Proyectos donde el usuario figura como miembro (cualquier rol).
 * Requiere que el doc tenga `miembrosUids` (ver migración para docs viejos).
 */
export const listProjectsForUser = async (uid: string): Promise<Project[]> => {
  const q = query(collection(db, "projects"), where("miembrosUids", "array-contains", uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Project, 'id'>) }));
};

export const subscribeProjectsForUser = (
  uid: string,
  callback: (projects: Project[]) => void
): Unsubscribe => {
  const q = query(collection(db, "projects"), where("miembrosUids", "array-contains", uid));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Project, 'id'>) })));
  });
};

/**
 * Proyectos de una organización, para su OWNER (Sub-fase E, Opción C:
 * "lectura amplia" sin necesitar ser miembro directo de cada proyecto —
 * ver esOwnerDeOrg en firestore.rules). A propósito NO es un reemplazo de
 * listProjectsForUser: un proyecto donde el owner SÍ es miembro real
 * aparece en ambas listas (hay que deduplicar por id si se combinan en
 * la UI). Solo tiene sentido llamarla si quien la ejecuta es owner de
 * `orgId` — si no, las rules la devuelven vacía, nunca un error.
 */
export const listProjectsForOrg = async (orgId: string): Promise<Project[]> => {
  const q = query(collection(db, "projects"), where("orgId", "==", orgId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Project, 'id'>) }));
};

/**
 * TODOS los proyectos de la plataforma, sin filtrar por membresía — solo
 * para la visibilidad global del Admin (ver "Vista Admin" en
 * MisProyectos.tsx). Firestore rules ya permiten `list` sin restricción de
 * `miembrosUids` cuando `isAdmin()`, así que esta query solo tiene sentido
 * (no será rechazada) si quien la ejecuta es admin — no usar para nadie más.
 * A propósito NO es un reemplazo de `listProjectsForUser`/`useUserProjects()`:
 * ese hook también lo consume "Mi Espacio" para su selector de contexto, que
 * NO debe llenarse con proyectos ajenos de toda la plataforma.
 */
export const listAllProjects = async (): Promise<Project[]> => {
  const snap = await getDocs(collection(db, "projects"));
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Project, 'id'>) }));
};

/**
 * Auditoría: un admin entró a un proyecto donde NO es miembro (ver
 * esAdminObservador en ProjectContext.tsx). Colección de solo-escritura
 * desde el cliente (firestore.rules: create si isAdmin(), read/update/delete
 * siempre false) — se revisa por consola/Admin SDK, no tiene UI propia
 * todavía.
 */
export const logAdminAccess = async (uid: string, projectId: string): Promise<void> => {
  const ref = doc(collection(db, "adminAccess"));
  await setDoc(ref, { uid, projectId, fecha: serverTimestamp() });
};

/**
 * Agrega un miembro nuevo al proyecto. Cualquier miembro actual puede
 * invitar (ver firestore.rules); el usuario invitado ya debe existir en
 * la plataforma (se busca antes por email con findPlatformUserByEmail).
 * Si el caller no especifica seccionesOcultas, se aplica el preset por
 * rol (ROLE_DEFAULT_SECCIONES_OCULTAS) — el admin lo ajusta después desde
 * PermisosMiembroModal.tsx si hace falta.
 */
export const addMember = async (projectId: string, member: ProjectMember) => {
  const ref = doc(db, "projects", projectId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("El proyecto no existe.");

  const current = (snap.data().miembros || []) as ProjectMember[];
  if (current.some(m => m.userId === member.userId)) {
    throw new Error("Ese usuario ya es miembro del proyecto.");
  }

  // Precheck del límite de miembros POR PROYECTO (proyecto_limites_organizacion)
  // — el freno real vive en firestore.rules, esto es solo para un mensaje
  // legible en vez de "Missing or insufficient permissions". Rediseño
  // 2026-08-19: el límite es del usuario que CREÓ este proyecto
  // (users/{creadoPor}.limiteMiembrosPorProyecto), no de la organización.
  const creadoPor = snap.data().creadoPor;
  if (creadoPor) {
    const creadorSnap = await getDoc(doc(db, "users", creadoPor));
    if (creadorSnap.exists()) {
      const limite = creadorSnap.data().limiteMiembrosPorProyecto ?? Infinity;
      if (current.length + 1 > limite) {
        throw new Error(`Este proyecto ya llegó al límite de ${limite} miembro(s). Contactá al administrador para ampliarlo.`);
      }
    }
  }

  const updates: Record<string, unknown> = {
    miembros: arrayUnion(member),
    miembrosUids: arrayUnion(member.userId)
  };
  if (member.rol === 'cliente') {
    updates.miembrosClienteUids = arrayUnion(member.userId);
  }

  // Batch con el doc inicial de permisos (subcolección propia, ver
  // MemberPermisos) — sin esto, Incidentes/Miembros/Editar Proyecto
  // seguirían sin protección real para cualquier miembro nuevo.
  const batch = writeBatch(db);
  batch.update(ref, updates);
  batch.set(doc(db, "projects", projectId, "permisos", member.userId), {
    seccionesOcultas: ROLE_DEFAULT_SECCIONES_OCULTAS[member.rol] ?? [],
    solucionesOcultas: []
  } as MemberPermisos);
  await batch.commit();
};

/** Lee los permisos de UN miembro puntual — `null` si nunca se configuraron (se trata como "nada oculto, no puede delegar"). */
export const getMemberPermisos = async (projectId: string, userId: string): Promise<MemberPermisos | null> => {
  const snap = await getDoc(doc(db, "projects", projectId, "permisos", userId));
  return snap.exists() ? (snap.data() as MemberPermisos) : null;
};

/**
 * true si `userId` puede ver/usar el botón "Permisos" de cualquier
 * miembro de este proyecto: es quien lo creó, o el dueño se lo delegó
 * explícitamente (ver MemberPermisos.puedeGestionarPermisos).
 */
export const puedeGestionarPermisosDeProyecto = (project: Project, userId: string, misPermisos: MemberPermisos | null): boolean =>
  project.creadoPor === userId || misPermisos?.puedeGestionarPermisos === true;

/**
 * Delega (o revoca) la facultad de gestionar permisos de otros miembros.
 * SOLO puede llamarlo quien creó el proyecto (ver firestore.rules) — ni
 * siquiera alguien ya delegado puede delegar a un tercero por esta vía.
 */
export const setPuedeGestionarPermisos = async (projectId: string, userId: string, valor: boolean): Promise<void> => {
  await setDoc(doc(db, "projects", projectId, "permisos", userId), { puedeGestionarPermisos: valor }, { merge: true });
};

/**
 * Cambia el rol de un miembro ya agregado (sin tener que sacarlo y
 * volver a invitarlo). Cualquier miembro que no sea "cliente" puede
 * hacerlo, sobre sí mismo o sobre otro — mismo criterio abierto que ya
 * usa `addMember` (ver firestore.rules, la regla de `update` de
 * projects/{id} ya lo permite sin cambios adicionales).
 *
 * Reescribe el array completo `miembros` (no hay forma atómica de tocar
 * solo un campo de un elemento de un array de mapas en Firestore) y
 * sincroniza `miembrosClienteUids` si el cambio entra o sale del rol
 * "cliente".
 */
export const updateMemberRole = async (projectId: string, userId: string, nuevoRol: ProjectRole) => {
  const ref = doc(db, "projects", projectId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("El proyecto no existe.");

  const miembros = (snap.data().miembros || []) as ProjectMember[];
  const actual = miembros.find(m => m.userId === userId);
  if (!actual) throw new Error("Ese usuario no es miembro del proyecto.");
  if (actual.rol === nuevoRol) return;

  const miembrosActualizados = miembros.map(m => m.userId === userId ? { ...m, rol: nuevoRol } : m);
  const updates: Record<string, unknown> = { miembros: miembrosActualizados };

  const eraCliente = actual.rol === 'cliente';
  const esClienteAhora = nuevoRol === 'cliente';
  if (eraCliente !== esClienteAhora) {
    updates.miembrosClienteUids = esClienteAhora ? arrayUnion(userId) : arrayRemove(userId);
  }

  await updateDoc(ref, updates);
};

/**
 * Guarda el cargo de organigrama y las responsabilidades de un miembro
 * (ver OrganigramaProyecto.tsx) — mismo mecanismo de "reescribir el array
 * completo" que updateMemberRole, porque Firestore no permite tocar un
 * solo campo de un elemento de un array de mapas. No toca `rol` ni
 * `miembrosClienteUids` — es puramente descriptivo.
 */
export const updateMemberOrganigrama = async (
  projectId: string,
  userId: string,
  cargoOrganigrama: string,
  responsabilidades: string
) => {
  const ref = doc(db, "projects", projectId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("El proyecto no existe.");

  const miembros = (snap.data().miembros || []) as ProjectMember[];
  if (!miembros.find(m => m.userId === userId)) throw new Error("Ese usuario no es miembro del proyecto.");

  const miembrosActualizados = miembros.map(m =>
    m.userId === userId ? { ...m, cargoOrganigrama: cargoOrganigrama.trim(), responsabilidades: responsabilidades.trim() } : m
  );
  await updateDoc(ref, { miembros: miembrosActualizados });
};

/**
 * Actualiza qué secciones/soluciones del sidebar ve este miembro.
 * Escribe SOLO projects/{id}/permisos/{userId} (no toca miembros[]) — ver
 * comentario de MemberPermisos para el porqué de vivir en subcolección
 * propia. `merge: true` para no pisar `puedeGestionarPermisos` si ya
 * estaba seteado (ese campo se toca aparte, con setPuedeGestionarPermisos).
 */
export const updateMemberPermissions = async (
  projectId: string,
  userId: string,
  seccionesOcultas: string[],
  solucionesOcultas: string[]
): Promise<void> => {
  await setDoc(
    doc(db, "projects", projectId, "permisos", userId),
    { seccionesOcultas, solucionesOcultas },
    { merge: true }
  );
};

/**
 * Inactiva a un miembro sin borrarlo: queda en `miembros[]` (con
 * `activo: false`) para conservar el historial/auditoría — horas
 * registradas, incidentes creados, etc. siguen atribuidos a él — pero
 * pierde acceso real al proyecto, porque se lo saca de `miembrosUids` (y
 * de `miembrosClienteUids` si era "cliente"), que es lo que de verdad
 * controla los permisos en firestore.rules (get/list del proyecto,
 * incidentes, credenciales compartidas, notas SAP compartidas, etc. — casi
 * todo chequea `miembrosUids`, no el array `miembros`). Mismo criterio de
 * reescritura completa del array que ya usa updateMemberRole (Firestore no
 * permite tocar un solo elemento de un array de mapas de forma atómica).
 */
export const deactivateMember = async (projectId: string, userId: string) => {
  const ref = doc(db, "projects", projectId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("El proyecto no existe.");

  const miembros = (snap.data().miembros || []) as ProjectMember[];
  const actual = miembros.find(m => m.userId === userId);
  if (!actual) throw new Error("Ese usuario no es miembro del proyecto.");
  if (actual.activo === false) return;

  const miembrosActualizados = miembros.map(m => m.userId === userId ? { ...m, activo: false } : m);
  const updates: Record<string, unknown> = {
    miembros: miembrosActualizados,
    miembrosUids: arrayRemove(userId)
  };
  if (actual.rol === 'cliente') {
    updates.miembrosClienteUids = arrayRemove(userId);
  }
  await updateDoc(ref, updates);
};

/** Contraparte de deactivateMember: le devuelve el acceso real (miembrosUids/miembrosClienteUids) y marca `activo: true`. */
export const reactivateMember = async (projectId: string, userId: string) => {
  const ref = doc(db, "projects", projectId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("El proyecto no existe.");

  const miembros = (snap.data().miembros || []) as ProjectMember[];
  const actual = miembros.find(m => m.userId === userId);
  if (!actual) throw new Error("Ese usuario no es miembro del proyecto.");
  if (actual.activo !== false) return;

  const miembrosActualizados = miembros.map(m => m.userId === userId ? { ...m, activo: true } : m);
  const updates: Record<string, unknown> = {
    miembros: miembrosActualizados,
    miembrosUids: arrayUnion(userId)
  };
  if (actual.rol === 'cliente') {
    updates.miembrosClienteUids = arrayUnion(userId);
  }
  await updateDoc(ref, updates);
};

/**
 * "Cuenta externa": true si el usuario figura con rol "cliente" en AL
 * MENOS UN proyecto (sin importar cuántos otros proyectos tenga con rol
 * interno). Se calcula en vivo con una query.
 *
 * A propósito NO filtra por `miembrosClienteUids` (la copia plana usada
 * por firestore.rules): esa copia puede desalinearse si un doc se tocó
 * por otra vía (edición manual en consola, datos de antes de que
 * updateMemberRole sincronizara este campo, etc. — ya pasó una vez).
 * En cambio usa `miembrosUids` (incluye a todo miembro sin importar el
 * rol, no se toca en cambios de rol, siempre confiable) para traer los
 * proyectos del usuario, y filtra en el cliente directamente sobre
 * `miembros[].rol`, la fuente de verdad. Cuesta un poco más (trae los
 * docs completos en vez de solo comprobar existencia) pero un usuario
 * normalmente participa en pocos proyectos.
 */
export const esClienteEnAlgunProyecto = async (uid: string): Promise<boolean> => {
  if (!uid) return false;
  const q = query(collection(db, "projects"), where("miembrosUids", "array-contains", uid));
  const snap = await getDocs(q);
  return snap.docs.some((d) => {
    const miembros = (d.data().miembros || []) as ProjectMember[];
    return miembros.some((m) => m.userId === uid && m.rol === 'cliente');
  });
};

/**
 * Crea una cuenta nueva (Firebase Auth + perfil) para alguien que todavía
 * no existe en la plataforma, y lo agrega como miembro del proyecto. Corre
 * server-side vía Admin SDK (Cloud Function callable) para no reemplazar
 * la sesión de quien está invitando, que es lo que pasaría si se llamara
 * a createUserWithEmailAndPassword directamente desde el navegador.
 */
export const createProjectMemberAccount = async (data: {
  projectId: string;
  email: string;
  rol: ProjectRole;
  nombreCompleto: string;
  // Sin contraseña temporal (lo normal desde 2026-10-26): la persona recibe un
  // correo con un enlace para crear la suya (ver createProjectMember.js).
  passwordTemporal?: string;
}): Promise<{ uid: string; correoEnviado: boolean }> => {
  const callable = httpsCallable(functionsClient, 'createProjectMember');
  const result = await callable({ ...data, appUrl: window.location.origin });
  return result.data as { uid: string; correoEnviado: boolean };
};

/**
 * Aviso por correo a alguien que YA tiene cuenta y fue agregado a un
 * proyecto (antes no se enteraba). Best-effort: no corta la invitación.
 */
export const notificarAgregadoAProyecto = async (email: string, proyectoNombre: string, projectId: string, invitadoPor: string) => {
  const esc = (t: string) => t.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);
  const url = `${window.location.origin}/proyecto/${projectId}`;
  await queueEmail({
    to: [email],
    subject: `Te agregaron al proyecto ${proyectoNombre} en OneHub`,
    html: `
      <p><strong>${esc(invitadoPor)}</strong> te agregó al proyecto <strong>${esc(proyectoNombre)}</strong> en OneHub.</p>
      <p><a href="${url}">Abrir el proyecto</a></p>
    `
  }).catch((err) => console.error('Error enviando el aviso de proyecto:', err));
};
